import { some } from "@prismicio/editor-support/Set"
import { isDefined } from "@prismicio/editor-support/Type"
import type { TextBlock } from "@prismicio/types-internal/lib/content"
import {
  RichTextConfig,
  type RichTextNodeType,
  RichTextNodeTypeCodec
} from "@prismicio/types-internal/lib/customtypes"
import type { Node as ProsemirrorNode, Schema } from "prosemirror-model"

import {
  DefaultTextExtension,
  getAllExtensions,
  Paragraph
} from "../extensions"
import {
  isListExtension,
  isTextExtension,
  type WidgetExtension
} from "./EditorExtension"

export function composeExtensions(config?: RichTextConfig) {
  if (!config || (!config?.single && !config?.multi)) return allExtensions()

  const nodeTypes = getNodeTypes(config)
  if (!nodeTypes?.size) return allExtensions()

  const configExtensions = selectConfigExtensions(nodeTypes)
  const extensions = addEssentialExtensions(configExtensions)

  return {
    extensions,
    defaultTextConverter: getDefaultTextConverter(extensions)
  }
}

function getNodeTypes(config: RichTextConfig): Set<RichTextNodeType> {
  const extensionTypes = (config.single || config.multi || "").split(/\s*,\s*/)
  const nodeTypes = extensionTypes
    .map((type) => {
      const decoded = RichTextNodeTypeCodec.decode(type)
      return decoded._tag === "Right" ? decoded.right : undefined
    })
    .filter(isDefined)

  return new Set(nodeTypes)
}

function selectConfigExtensions(nodeTypes: Set<RichTextNodeType>) {
  const allExtensions = getAllExtensions(nodeTypes)

  return allExtensions.filter((extension) =>
    some(nodeTypes, (nodeType) =>
      extension.richTextNodeTypes.includes(nodeType)
    )
  )
}

function addEssentialExtensions(configExtensions: WidgetExtension[]) {
  const set = new Set(configExtensions)

  // ListExtension requires Paragraph as a dependency.
  if (some(set, isListExtension)) set.add(Paragraph)

  // If we still don't have any Text based extensions after all this, add the default one.
  if (!some(set, isTextExtension)) set.add(DefaultTextExtension)

  return [...set]
}

function getDefaultTextConverter(extensions: WidgetExtension[]) {
  const defaultText = extensions.find(isTextExtension) ?? DefaultTextExtension

  function converter(schema: Schema) {
    return (block: TextBlock): ProsemirrorNode | undefined => {
      return defaultText.converter.fromPrismic(schema)({
        ...block,
        type: defaultText.richTextNodeTypes[0] ?? "heading1"
      })
    }
  }

  return converter
}

function allExtensions() {
  return {
    extensions: getAllExtensions(),
    defaultTextConverter: undefined
  }
}
